import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from '@/stores/toastStore'
import recruiterService, { SavedCandidate } from '@/services/recruiterService'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import { Trash2, Edit, Save, X, Eye, Search, Filter, Send, UserCheck } from 'lucide-react'
import Modal from '@/components/common/Modal'
import Textarea from '@/components/common/Textarea'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'

export default function Contacts() {
  const { t } = useTranslation('recruiter')
  const navigate = useNavigate()
  const [savedCandidates, setSavedCandidates] = useState<SavedCandidate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null)
  const [editingNotes, setEditingNotes] = useState<string | null>(null)
  const [currentNotes, setCurrentNotes] = useState('')
  const [filterQuery, setFilterQuery] = useState('')
  const [selectedJobTitle, setSelectedJobTitle] = useState<string>('')
  const [selectedLocation, setSelectedLocation] = useState<string>('')

  const [isLoadingProfile, setIsLoadingProfile] = useState<string | null>(null)

  useEffect(() => {
    fetchSavedCandidates()
  }, [])

  const fetchSavedCandidates = async () => {
    try {
      setLoading(true)
      const { savedCandidates } = await recruiterService.getSavedCandidates()
      setSavedCandidates(savedCandidates.filter((sc) => sc.source === 'CONTACT_REVEAL'))
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch contacts.')
      toast.error(err.response?.data?.error || 'Failed to fetch contacts.')
    } finally {
      setLoading(false)
    }
  }

  const handleRemove = async (savedId: string) => {
    try {
      await recruiterService.removeSavedCandidate(savedId)
      setSavedCandidates(savedCandidates.filter((sc) => sc.id !== savedId))
      toast.success('Contact removed.')
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to remove contact.')
    } finally {
      setShowDeleteModal(null)
    }
  }

  const handleUpdateNotes = async (savedId: string) => {
    try {
      const updatedCandidate = await recruiterService.updateSavedCandidateNotes(
        savedId,
        currentNotes
      )
      setSavedCandidates(
        savedCandidates.map((sc) =>
          sc.id === savedId ? { ...sc, notes: updatedCandidate.notes } : sc
        )
      )
      toast.success('Notes updated successfully.')
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to update notes.')
    } finally {
      setEditingNotes(null)
      setCurrentNotes('')
    }
  }

  const handleViewProfile = async (candidateId: string) => {
    setIsLoadingProfile(candidateId)
    try {
      const fullCandidate = await recruiterService.viewCandidate(candidateId)
      let contactInfo = null
      if (fullCandidate.contactRevealed) {
        try {
          const { contact } = await recruiterService.revealContact(candidateId)
          contactInfo = contact
        } catch {
          // already revealed but could not re-fetch contact
        }
      }
      navigate(`/recruiter/candidates/${candidateId}`, {
        state: { candidate: fullCandidate, contactInfo },
      })
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data
        ?.error
      toast.error(errorMessage || 'Failed to load candidate profile')
    } finally {
      setIsLoadingProfile(null)
    }
  }

  const uniqueJobTitles = useMemo(() => {
    const titles = savedCandidates
      .map((saved) => saved.candidate.jobTitle)
      .filter((title): title is string => !!title)
    return Array.from(new Set(titles)).sort()
  }, [savedCandidates])

  const uniqueLocations = useMemo(() => {
    const locations = savedCandidates
      .map((saved) => saved.candidate.location)
      .filter((location): location is string => !!location)
    return Array.from(new Set(locations)).sort()
  }, [savedCandidates])

  const filteredCandidates = savedCandidates.filter((saved) => {
    const query = filterQuery.toLowerCase()

    const matchesTextSearch =
      !filterQuery ||
      saved.candidate.firstName.toLowerCase().includes(query) ||
      saved.candidate.lastName.toLowerCase().includes(query) ||
      saved.candidate.jobTitle?.toLowerCase().includes(query) ||
      saved.candidate.location?.toLowerCase().includes(query) ||
      saved.notes?.toLowerCase().includes(query)

    const matchesJobTitle = !selectedJobTitle || saved.candidate.jobTitle === selectedJobTitle
    const matchesLocation = !selectedLocation || saved.candidate.location === selectedLocation

    return matchesTextSearch && matchesJobTitle && matchesLocation
  })

  const clearAllFilters = () => {
    setFilterQuery('')
    setSelectedJobTitle('')
    setSelectedLocation('')
  }

  const hasActiveFilters = filterQuery || selectedJobTitle || selectedLocation

  if (loading) {
    return (
      <div className="container-custom py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container-custom py-12 text-center">
        <p className="text-red-500">{error}</p>
      </div>
    )
  }

  return (
    <div className="container-custom py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Contacts</h1>

      {savedCandidates.length > 0 && (
        <Card className="mb-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-gray-700 mb-2">
              <Filter className="w-5 h-5" />
              <span className="font-medium">Filter Contacts</span>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <Input
                  type="text"
                  placeholder="Name or notes..."
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  icon={<Search className="w-5 h-5" />}
                />
              </div>

              <Select
                label="Job Title"
                value={selectedJobTitle}
                onChange={(e) => setSelectedJobTitle(e.target.value)}
                options={uniqueJobTitles.map((title) => ({ value: title, label: title }))}
              />

              <Select
                label="Location"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                options={uniqueLocations.map((location) => ({ value: location, label: location }))}
              />
            </div>

            {hasActiveFilters && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {filteredCandidates.length} of {savedCandidates.length} contacts
                </div>
                <Button variant="secondary" size="sm" onClick={clearAllFilters}>
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {filteredCandidates.length === 0 ? (
        <Card className="text-center py-16">
          <UserCheck className="w-16 h-16 text-blue-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {hasActiveFilters ? 'No Matching Contacts' : 'No Revealed Contacts'}
          </h2>
          <p className="text-gray-600 mb-6">
            {hasActiveFilters
              ? 'Adjust your filter or clear it to see all contacts.'
              : "Candidates whose contacts you've revealed will appear here."}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredCandidates.map((saved) => (
            <Card key={saved.id}>
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex gap-4 flex-shrink-0">
                  <div className="flex-shrink-0">
                    {saved.candidate.profilePhotoUrl ? (
                      <img
                        src={saved.candidate.profilePhotoUrl}
                        alt={`${saved.candidate.firstName} ${saved.candidate.lastName}`}
                        className="w-20 h-20 rounded-full rounded-avatar object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full rounded-avatar bg-gray-200 flex items-center justify-center">
                        <span className="text-2xl font-semibold text-gray-600">
                          {saved.candidate.firstName[0]}
                          {saved.candidate.lastName[0]}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col justify-center min-w-[200px]">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {saved.candidate.firstName} {saved.candidate.lastName}
                    </h3>
                    <p className="text-gray-700 font-medium">{saved.candidate.jobTitle}</p>
                    <p className="text-sm text-gray-500">{saved.candidate.location}</p>
                  </div>
                </div>

                <div className="flex-grow">
                  {editingNotes === saved.id ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 h-full">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Notes
                      </label>
                      <Textarea
                        value={currentNotes}
                        onChange={(e) => setCurrentNotes(e.target.value)}
                        placeholder="Add your notes about this candidate..."
                        className="w-full min-h-[80px]"
                      />
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => handleUpdateNotes(saved.id)}
                        >
                          <Save className="w-4 h-4 mr-1" /> Save Notes
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditingNotes(null)}>
                          <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`rounded-lg p-3 h-full flex flex-col ${saved.notes ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200 border-dashed'}`}
                    >
                      <div className="flex items-start justify-between gap-2 flex-grow">
                        <div className="flex-grow">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            Notes
                          </p>
                          <p
                            className={`text-sm ${saved.notes ? 'text-gray-800' : 'text-gray-400 italic'}`}
                          >
                            {saved.notes ||
                              'No notes yet. Click "Add" to add your thoughts about this candidate.'}
                          </p>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setEditingNotes(saved.id)
                            setCurrentNotes(saved.notes || '')
                          }}
                          className="flex-shrink-0"
                        >
                          <Edit className="w-4 h-4 mr-1" /> {saved.notes ? 'Edit' : 'Add'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex lg:flex-col gap-2 flex-shrink-0">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      const name = `${saved.candidate.firstName} ${saved.candidate.lastName}`
                      navigate(
                        `/recruiter/job-offers/new?candidateId=${saved.candidateId}&candidateName=${encodeURIComponent(name)}`
                      )
                    }}
                    className="flex-1 lg:flex-initial"
                  >
                    <Send className="w-4 h-4 mr-1" /> Send Offer
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleViewProfile(saved.candidateId)}
                    isLoading={isLoadingProfile === saved.candidateId}
                    disabled={isLoadingProfile !== null}
                    className="flex-1 lg:flex-initial flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    {t('profileReveal.search.viewProfile')}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowDeleteModal(saved.id)}
                    className="flex-1 lg:flex-initial"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showDeleteModal && (
        <Modal
          title="Confirm Removal"
          onClose={() => setShowDeleteModal(null)}
          isOpen={!!showDeleteModal}
        >
          <p className="text-gray-700 mb-6">Are you sure you want to remove this contact?</p>
          <div className="flex justify-end gap-4">
            <Button variant="secondary" onClick={() => setShowDeleteModal(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => handleRemove(showDeleteModal)}>
              Remove
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
